// Libraries
import 'nodent-runtime'
import 'core/polyfills'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import * as mobx from 'mobx'
import mobxFormatters from 'mobx-formatters'
import { defaultsDeep, omit } from 'lodash'
import { enableLogging } from 'mobx-logger'
import { Provider } from 'mobx-react'
import { observerBatching } from 'mobx-react-lite'
import { initProvider, ccpProvider as globalCcpProvider } from 'core/CcpProvider'
import { rgba } from 'core/utils'
import CONFIG from 'core/globals'
import OptionsContext from 'core/OptionsContext'
import { mapTranslations } from 'core/translations'
import ChatWindowUser from 'components/ChatWindowUser'
import { getCookie } from 'core/cookie'

// to avoid unexpected behaviour https://github.com/mobxjs/mobx-react-lite/#observer-batching
// + ensuring it will not be tree-shaken. can be removed if updated to 2.2+
observerBatching(ReactDOM.unstable_batchedUpdates)

// Debugging
const env = 'MS-production'
if ((`${env}` !== 'MS-production' || getCookie('mobxLogger') === '1') && !window.mobxLoggerActive) {
  window.mobxLoggerActive = true
  mobxFormatters(mobx)
  enableLogging({
    action: true,
    reaction: false,
    transaction: true,
    compute: false,
  })
}

const init = (() => {
  let store, actions
  return options => {
    //do not merge if commented out;only dev works without it;
    options = omit(options, ['api'])
    defaultsDeep(options, CONFIG)
    Object.assign(CONFIG, options) // still used in some rare cases

    // don't load if already loaded or on blacklist pages
    if (!window.__STATE_WIDGET && !CONFIG.blackList.includes(location.pathname)) {
      store = require('./store').default

      actions = require('./actions')
      const ccpProvider = initProvider(options)

      // For debugging
      window.__STATE_WIDGET = store
      window.__PROVIDER = ccpProvider
      actions.auth.setSession({ token: CONFIG.userToken })
    }
    return { store, actions }
  }
})()

const ChatWidget = ({ options }) => {
  const { store, actions } = init(options)
  const [optionsCtx, _setOptionsCtx] = useState(options)

  const setOptionsCtx = opts => {
    if (opts.translations) {
      opts.translations = mapTranslations(opts)
    }
    Object.assign(CONFIG, opts)
    _setOptionsCtx(opts)
  }

  useEffect(() => {
    if (options.translations) {
      options.translations = mapTranslations(options)
    }
    _setOptionsCtx(options)
  }, [options])

  return (
    <Provider store={store} actions={actions}>
      <OptionsContext.Provider value={[optionsCtx, setOptionsCtx]}>
        <ChatWindowUser options={options}/>
      </OptionsContext.Provider>
    </Provider>
  )
}

window.CCP = {
  initWidget(target, options = {}) {
    ReactDOM.render(<ChatWidget options={options}/>, target)
  },
  rgba,
  disconectFromWS() {
    if (globalCcpProvider.websocket) {
      globalCcpProvider.websocket.disconnect()
      globalCcpProvider.websocket = null
    }

    if (window.__PROVIDER && window.__PROVIDER.websocket) {
      window.__PROVIDER.websocket.disconnect()
      window.__PROVIDER.websocket = null
    }

    if (window.__STATE_WIDGET) {
      window.__STATE_WIDGET = null
    }
  },
}

const ccpLoadedEvent = new CustomEvent(
  'ccp-widget-loaded',
  { detail: { message: 'CCP is loaded and contains the function.' } }
);

document.dispatchEvent(ccpLoadedEvent);

export default ChatWidget
export { rgba }
